import React from 'react'
import PropTypes from 'prop-types'

const formatTimeRemaining = ms => {
  if (ms == null || ms <= 0) return 'Expired'
  const totalSeconds = Math.floor(ms / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const parts = []
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0) parts.push(`${minutes}m`)
  parts.push(`${seconds}s`)
  return parts.join(' ')
}

const styles = {
  container: {
    maxWidth: '48rem',
    margin: '2rem auto',
    padding: '2rem',
    fontFamily: 'sans-serif',
  },
  heading: {
    fontSize: '1.5rem',
    fontWeight: 600,
    marginBottom: '1.5rem',
    color: '#333',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    marginBottom: '0.25rem',
    color: '#555',
  },
  input: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    border: '1px solid #ccc',
    borderRadius: '0.25rem',
    marginBottom: '1rem',
    boxSizing: 'border-box',
    fontFamily: 'monospace',
  },
  button: {
    padding: '0.625rem 1.25rem',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#fff',
    backgroundColor: '#0066cc',
    border: 'none',
    borderRadius: '0.25rem',
    cursor: 'pointer',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  card: {
    marginTop: '1.5rem',
    padding: '1rem',
    border: '1px solid #e0e0e0',
    borderRadius: '0.5rem',
    backgroundColor: '#f9f9f9',
  },
  cardHeading: {
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: '0.75rem',
    color: '#333',
  },
  linkUrl: {
    wordBreak: 'break-all',
    color: '#0066cc',
    fontFamily: 'monospace',
    fontSize: '0.875rem',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.375rem 0',
    fontSize: '0.875rem',
    borderBottom: '1px solid #eee',
  },
  rowLabel: {
    fontWeight: 500,
    color: '#555',
  },
  rowValue: {
    color: '#333',
    fontFamily: 'monospace',
  },
  error: {
    marginTop: '1rem',
    padding: '0.75rem',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '0.25rem',
    color: '#b91c1c',
    fontSize: '0.875rem',
  },
  expired: {
    marginTop: '1rem',
    padding: '0.75rem',
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: '0.25rem',
    color: '#92400e',
    fontSize: '0.875rem',
  },
  badge: {
    display: 'inline-block',
    padding: '0.125rem 0.5rem',
    borderRadius: '1rem',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  badgeValid: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
  },
  badgeExpired: {
    backgroundColor: '#fde68a',
    color: '#92400e',
  },
  subtitle: {
    fontSize: '0.875rem',
    color: '#777',
    marginBottom: '1.5rem',
  },
}

const ExperiencePlayerTestComponent = ({
  lessonIdInput,
  onLessonIdChange,
  onGenerate,
  link,
  isCreating,
  isExpired,
  error,
  timeUntilExpiry,
}) => {
  const hasActiveLink = link && !isExpired

  return (
    <div style={styles.container}>
      <h1 style={styles.heading}>Experience Player - Launch Link Test</h1>
      <p style={styles.subtitle}>
        Prototype page for generating and testing experience-player launch
        links.
      </p>

      <div>
        <label htmlFor="lessonId" style={styles.label}>
          Lesson ID
        </label>
        <input
          id="lessonId"
          onChange={onLessonIdChange}
          placeholder="Enter lesson ID"
          style={styles.input}
          type="text"
          value={lessonIdInput}
        />
      </div>

      <button
        disabled={isCreating || !lessonIdInput}
        onClick={onGenerate}
        style={{
          ...styles.button,
          ...(isCreating ? styles.buttonDisabled : {}),
        }}
        type="button"
      >
        {isCreating ? 'Generating...' : 'Generate Launch Link'}
      </button>

      {error && (
        <div style={styles.error}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {isExpired && !link && (
        <div style={styles.expired}>
          Link has expired. Generate a new one above.
        </div>
      )}

      {link && (
        <div style={styles.card}>
          <h2 style={styles.cardHeading}>
            Link Details{' '}
            <span
              style={{
                ...styles.badge,
                ...(hasActiveLink ? styles.badgeValid : styles.badgeExpired),
              }}
            >
              {hasActiveLink ? 'Active' : 'Expired'}
            </span>
          </h2>

          <div style={styles.row}>
            <span style={styles.rowLabel}>URL</span>
            <a
              href={link.url}
              rel="noopener noreferrer"
              style={styles.linkUrl}
              target="_blank"
            >
              {link.url}
            </a>
          </div>

          <div style={styles.row}>
            <span style={styles.rowLabel}>Link ID</span>
            <span style={styles.rowValue}>{link.linkId}</span>
          </div>

          <div style={styles.row}>
            <span style={styles.rowLabel}>Lesson ID</span>
            <span style={styles.rowValue}>{link.lessonId}</span>
          </div>

          <div style={styles.row}>
            <span style={styles.rowLabel}>Expires At</span>
            <span style={styles.rowValue}>
              {link.expiresAt
                ? new Date(link.expiresAt).toLocaleString()
                : 'Never'}
            </span>
          </div>

          <div style={styles.row}>
            <span style={styles.rowLabel}>Time Remaining</span>
            <span style={styles.rowValue}>
              {timeUntilExpiry != null
                ? formatTimeRemaining(timeUntilExpiry)
                : 'N/A'}
            </span>
          </div>

          <div style={{ ...styles.row, borderBottom: 'none' }}>
            <span style={styles.rowLabel}>Password Protected</span>
            <span style={styles.rowValue}>
              {link.hasPassword ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

ExperiencePlayerTestComponent.propTypes = {
  error: PropTypes.string,
  isCreating: PropTypes.bool,
  isExpired: PropTypes.bool,
  lessonIdInput: PropTypes.string.isRequired,
  link: PropTypes.shape({
    linkId: PropTypes.string,
    url: PropTypes.string,
    expiresAt: PropTypes.string,
    lessonId: PropTypes.string,
    hasPassword: PropTypes.bool,
    isValid: PropTypes.bool,
  }),
  onGenerate: PropTypes.func.isRequired,
  onLessonIdChange: PropTypes.func.isRequired,
  timeUntilExpiry: PropTypes.number,
}

export default ExperiencePlayerTestComponent
